import React, { useState, useEffect, useRef } from "react";
import { apiCallGeneric } from "../service/api-service";
import { ReactComponent as ArrowDownIcon } from "../../assets/svg/arrow-down.svg";
import { ReactComponent as ArrowUpIcon } from "../../assets/svg/arrow-up.svg";

const CustomDropdownWithSearch = ({
  searchId = -1,
  onChangeIndex = {},
  onDropdownChange,
  apiUrl,
  placeholder,
  selectedValue,
  selectedOption,
  params = {},
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  const fetchOptions = async () => {
    setLoading(true);
    setError(null);
    try {
      if (apiUrl) {
        const requestParams = {
          ...params,
          term: searchTerm,
        };
        const response = await apiCallGeneric("GET", apiUrl, null, {
          params: requestParams,
        });

        if (
          response !== undefined &&
          response.status === 200 &&
          response.data.success === true
        ) {
          setOptions(response.data.suggestionlist);
          setFilteredOptions(response.data.suggestionlist);
        } else {
          setOptions([]);
          setFilteredOptions([]);
        }
      }
    } catch (err) {
      console.error("Error fetching options:", err);
      setError("No options found.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm === "" || searchTerm === null) {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }
  }, [searchTerm, options]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (disabled) return; // ✅ prevent dropdown from opening
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      fetchOptions();
    }
  };

  const selectOption = (option) => {
    if (Object.keys(onChangeIndex).length === 0) {
      onDropdownChange(option);
    } else {
      onDropdownChange(option, onChangeIndex);
    }
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative", height: "40px" }}>
      {/* Dropdown Header */}
      <div
        onClick={toggleDropdown}
        style={{
          height: "40px",
          width: "100%",
          padding: "10px 12px",
          border: isOpen ? "1px solid #3d4af5" : "1px solid #e5e5e5",
          borderRadius: "10px",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: disabled ? "#f8f8f8" : "#ffffff",
          color: disabled
            ? "#b0b0b0"
            : selectedOption && selectedOption.label
              ? "#172b4d"
              : "#98a1b0",
          fontSize: "14px",
          fontWeight: "400",
          transition: "all 0.2s ease",
          boxShadow: isOpen ? "0 0 0 3px rgba(61, 74, 245, 0.1)" : "none",
        }}
      >
        <span
          style={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            flex: 1,
          }}
        >
          {selectedOption && selectedOption.label
            ? selectedOption.label
            : placeholder}
        </span>
        <span
          style={{
            marginLeft: "10px",
            display: "flex",
            alignItems: "center",
            transition: "transform 0.2s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ArrowDownIcon style={{ width: "16px", height: "16px" }} />
        </span>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            border: "1px solid #e5e5e5",
            borderRadius: "12px",
            background: "#fff",
            zIndex: 1000,
            maxHeight: "280px",
            overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            animation: "slideDown 0.2s ease",
          }}
        >
          {/* Search Input */}
          <div
            style={{
              padding: "12px",
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
              borderBottom: "1px solid #f0f0f0",
              borderRadius: "12px 12px 0 0",
            }}
          >
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              disabled={disabled}
              autoFocus
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                boxSizing: "border-box",
                color: "#172b4d",
                background: disabled ? "#f8f8f8" : "#fff",
                fontSize: "14px",
                outline: "none",
                transition: "border-color 0.2s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3d4af5")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e5e5")}
            />
          </div>

          {/* Options */}
          <div style={{ padding: "8px 0" }}>
            {loading ? (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: "#98a1b0",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                ></div>
                <span>Loading...</span>
              </div>
            ) : error ? (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: "#ff4444",
                  fontSize: "14px",
                }}
              >
                {error}
              </div>
            ) : filteredOptions && filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected =
                  selectedOption && selectedOption.value === option.value;
                return (
                  <div
                    key={index}
                    onClick={() => selectOption(option)}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "#f8f9fb";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                    style={{
                      padding: "10px 16px",
                      cursor: "pointer",
                      background: isSelected ? "#f8f9fb" : "transparent",
                      color: "#172b4d",
                      fontSize: "14px",
                      fontWeight: "400",
                      textAlign: "left",
                      transition: "all 0.15s ease",
                      borderLeft: isSelected
                        ? "3px solid #3d4af5"
                        : "3px solid transparent",
                    }}
                  >
                    {option.label}
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: "#98a1b0",
                  fontSize: "14px",
                }}
              >
                <div style={{ marginBottom: "4px", fontSize: "16px" }}>🔍</div>
                No records found.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdownWithSearch;
